import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { 
  Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, 
  Clock, Undo2, Download, RefreshCw, Loader2, History,
  FileWarning, Users, Search, Trash2
} from 'lucide-react';
import { ImportUpload } from './ImportUpload';
import { ImportPreview } from './ImportPreview';
import { ImportReview } from './ImportReview';

const API_URL = window.location.origin;

export default function ImportTitles() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    loadDashboard();
    loadBatches();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await api.get('/finance/import-titles/dashboard');
      setDashboard(res.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBatches = async () => {
    try {
      const res = await api.get('/finance/import-titles/batches?limit=10');
      setBatches(res.data.batches || []);
    } catch (error) {
      console.error('Erro ao carregar lotes:', error);
    }
  };

  const handleUploadComplete = (result) => {
    setUploadResult(result);
    setActiveTab('preview');
    loadBatches();
    loadDashboard();
  };

  const handleConfirmImport = async (batchId, force = false, linkRequired = true) => {
    try {
      const res = await api.post(`/finance/import-titles/confirm/${batchId}?force=${force}&link_required=${linkRequired}`);
      toast.success(res.data.message);
      loadBatches();
      loadDashboard();
      setActiveTab('dashboard');
      setUploadResult(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao confirmar importação');
    }
  };

  const handleRollback = async (batchId) => {
    if (!window.confirm('Tem certeza que deseja reverter este lote? Todos os títulos importados serão removidos.')) {
      return;
    }
    try {
      const res = await api.post(`/finance/import-titles/batches/${batchId}/rollback`);
      toast.success(res.data.message);
      loadBatches();
      loadDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reverter lote');
    }
  };

  const [cleaning, setCleaning] = useState(false);
  const handleCleanup = async () => {
    const ok = window.confirm(
      'Deseja apagar todo o histórico de importação, pendências de vínculo e duplicidades?\n\n' +
      'Os títulos oficiais já promovidos NÃO serão apagados.'
    );
    if (!ok) return;
    setCleaning(true);
    try {
      const res = await api.delete('/financial/import/cleanup');
      const total = res?.data?.total ?? 0;
      const intact = res?.data?.titles_intact ?? '?';
      toast.success(`${total} documentos de staging removidos · ${intact} títulos oficiais intactos`);
      // Zera tudo no front
      setUploadResult(null);
      setSelectedBatch(null);
      setBatches([]);
      await Promise.all([loadDashboard(), loadBatches()]);
      setActiveTab('dashboard');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Erro ao limpar histórico de importação';
      toast.error(msg);
    } finally {
      setCleaning(false);
    }
  };

  // Reverter importação — remove TODOS os títulos importados do Motor Financeiro
  const [revertOpen, setRevertOpen] = useState(false);
  const [revertDryRun, setRevertDryRun] = useState(null);   // resultado do dry_run
  const [revertConfirmText, setRevertConfirmText] = useState('');
  const [revertBusy, setRevertBusy] = useState(false);

  const openRevertModal = async () => {
    setRevertConfirmText('');
    setRevertDryRun(null);
    setRevertOpen(true);
    setRevertBusy(true);
    try {
      const res = await api.post('/financial/import/revert-all?dry_run=true');
      setRevertDryRun(res.data);
    } catch (error) {
      const d = error?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Falha ao pré-visualizar reversão'));
      setRevertOpen(false);
    } finally {
      setRevertBusy(false);
    }
  };

  const handleRevertApply = async () => {
    if ((revertConfirmText || '').trim().toUpperCase() !== 'REVERTER') {
      toast.error('Digite REVERTER para confirmar');
      return;
    }
    setRevertBusy(true);
    try {
      const res = await api.post(
        `/financial/import/revert-all?dry_run=false&confirm_text=REVERTER`
      );
      const d = res.data || {};
      toast.success(
        `${d.titles_reverted || 0} títulos importados removidos · ${d.skipped_manual || 0} manuais preservados`
      );
      setRevertOpen(false);
      setRevertDryRun(null);
      setRevertConfirmText('');
      // Atualiza dashboard/lotes
      setUploadResult(null);
      setSelectedBatch(null);
      await Promise.all([loadDashboard(), loadBatches()]);
    } catch (error) {
      const d = error?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Falha ao reverter importação'));
    } finally {
      setRevertBusy(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pendente', variant: 'secondary', icon: Clock },
      simulated: { label: 'Simulado', variant: 'outline', icon: Search },
      confirmed: { label: 'Confirmado', variant: 'default', icon: CheckCircle2 },
      rolled_back: { label: 'Revertido', variant: 'destructive', icon: Undo2 },
      partial: { label: 'Parcial', variant: 'warning', icon: AlertTriangle }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="import-titles-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-green-500 bg-clip-text text-transparent">
            Central de Importação de Títulos
          </h1>
          <p className="text-muted-foreground">
            Importe títulos financeiros de planilhas externas com segurança e auditoria
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => window.open(`${API_URL}/api/finance/import-titles/download-template`, '_blank')}
          >
            <Download className="w-4 h-4 mr-2" />
            Modelo
          </Button>
          {(() => {
            try {
              const u = JSON.parse(localStorage.getItem('user') || 'null');
              return u?.role === 'admin';
            } catch { return false; }
          })() && (
            <>
              <Button
                variant="outline"
                onClick={openRevertModal}
                disabled={revertBusy}
                data-testid="btn-revert-import"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                {revertBusy && !revertOpen ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
                Reverter Importação
              </Button>
              <Button
                variant="outline"
                onClick={handleCleanup}
                disabled={cleaning}
                data-testid="btn-cleanup-import"
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
              >
                {cleaning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                Limpar Histórico
              </Button>
            </>
          )}
          <Button
            onClick={() => setActiveTab('upload')}
            className="bg-gradient-to-r from-purple-600 to-green-500 hover:from-purple-700 hover:to-green-600"
          >
            <Upload className="w-4 h-4 mr-2" />
            Novo Upload
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="dashboard" className="rounded-lg">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="upload" className="rounded-lg">
            <Upload className="w-4 h-4 mr-2" />
            Upload
          </TabsTrigger>
          {uploadResult && (
            <TabsTrigger value="preview" className="rounded-lg">
              <Search className="w-4 h-4 mr-2" />
              Prévia
            </TabsTrigger>
          )}
          <TabsTrigger value="review" className="rounded-lg">
            <FileWarning className="w-4 h-4 mr-2" />
            Revisão
          </TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg">
            <History className="w-4 h-4 mr-2" />
            Histórico
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total de Lotes</p>
                    <p className="text-2xl font-bold">{dashboard?.total_batches || 0}</p>
                  </div>
                  <FileSpreadsheet className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pendências de Vínculo</p>
                    <p className="text-2xl font-bold">{dashboard?.pending_links || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-orange-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Duplicidades</p>
                    <p className="text-2xl font-bold">{dashboard?.duplicates || 0}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-red-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Erros</p>
                    <p className="text-2xl font-bold">{dashboard?.errors || 0}</p>
                  </div>
                  <FileWarning className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Last Batch */}
          {dashboard?.last_batch && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Último Lote</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <p className="font-medium">{dashboard.last_batch.filename}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(dashboard.last_batch.created_at).toLocaleString('pt-BR')}
                    </p>
                    <div className="flex gap-2 items-center">
                      {getStatusBadge(dashboard.last_batch.status)}
                      <span className="text-sm">
                        {dashboard.last_batch.total_rows} linhas | 
                        R$ {dashboard.last_batch.total_amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {dashboard.last_batch.status === 'simulated' && (
                      <Button 
                        size="sm"
                        onClick={() => {
                          setUploadResult({ batch_id: dashboard.last_batch.id });
                          setActiveTab('preview');
                        }}
                      >
                        Ver Prévia
                      </Button>
                    )}
                    {dashboard.last_batch.status === 'confirmed' && (
                      <Button 
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRollback(dashboard.last_batch.id)}
                      >
                        <Undo2 className="w-4 h-4 mr-1" />
                        Reverter
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ações Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button onClick={() => setActiveTab('upload')}>
                <Upload className="w-4 h-4 mr-2" />
                Novo Upload
              </Button>
              <Button variant="outline" onClick={() => setActiveTab('history')}>
                <History className="w-4 h-4 mr-2" />
                Ver Lotes
              </Button>
              <Button variant="outline" onClick={() => setActiveTab('review')}>
                <FileWarning className="w-4 h-4 mr-2" />
                Rever Pendências
              </Button>
              <Button variant="outline" onClick={loadDashboard}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Atualizar
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Upload Tab */}
        <TabsContent value="upload">
          <ImportUpload onUploadComplete={handleUploadComplete} />
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview">
          {uploadResult && (
            <ImportPreview 
              batchId={uploadResult.batch_id}
              onConfirm={handleConfirmImport}
              onCancel={() => {
                setUploadResult(null);
                setActiveTab('dashboard');
              }}
            />
          )}
        </TabsContent>

        {/* Review Tab */}
        <TabsContent value="review">
          <ImportReview onResolve={() => {
            loadDashboard();
            loadBatches();
          }} />
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Importações</CardTitle>
              <CardDescription>Todos os lotes de importação realizados</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {batches.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Nenhuma importação realizada.
                  </p>
                ) : (
                  batches.map((batch) => (
                    <div 
                      key={batch.id}
                      className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4 text-purple-500" />
                          <span className="font-medium">{batch.filename}</span>
                          {getStatusBadge(batch.status)}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {new Date(batch.created_at).toLocaleString('pt-BR')}
                        </p>
                        <div className="flex gap-4 text-sm">
                          <span>Total: {batch.total_rows}</span>
                          <span className="text-green-600">Válidos: {batch.valid_rows}</span>
                          <span className="text-yellow-600">Alertas: {batch.warning_rows}</span>
                          <span className="text-red-600">Erros: {batch.error_rows}</span>
                          {batch.imported_rows > 0 && (
                            <span className="text-purple-600">Importados: {batch.imported_rows}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2 md:mt-0">
                        {batch.status === 'simulated' && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                setUploadResult({ batch_id: batch.id });
                                setActiveTab('preview');
                              }}
                            >
                              Ver Prévia
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => handleConfirmImport(batch.id)}
                              data-testid={`btn-confirm-${batch.id}`}
                            >
                              Confirmar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              title="Importar mesmo sem vínculo aluno/responsável (cria com origin=importacao-pendente-link)"
                              onClick={() => {
                                if (window.confirm('Importar TODAS as linhas, mesmo sem vínculo de aluno/responsável?\n\nOs títulos serão criados com origin "importacao-pendente-link" e podem ser vinculados depois.')) {
                                  handleConfirmImport(batch.id, true, false);
                                }
                              }}
                              data-testid={`btn-confirm-soft-${batch.id}`}
                            >
                              Importar Pendentes
                            </Button>
                          </>
                        )}
                        {batch.status === 'confirmed' && (
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => handleRollback(batch.id)}
                          >
                            <Undo2 className="w-4 h-4 mr-1" />
                            Reverter
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal forte: Reverter Importação */}
      <Dialog open={revertOpen} onOpenChange={(o) => !revertBusy && setRevertOpen(o)}>
        <DialogContent className="max-w-lg" data-testid="revert-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" /> Reverter Importação
            </DialogTitle>
            <DialogDescription>
              Esta ação irá remover <strong>todos os títulos importados</strong> do Motor Financeiro.
              Títulos manuais <strong>não</strong> serão afetados.
            </DialogDescription>
          </DialogHeader>

          {revertBusy && !revertDryRun ? (
            <div className="py-8 flex items-center justify-center text-sm text-slate-600">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Calculando impacto…
            </div>
          ) : revertDryRun ? (
            <div className="space-y-3 py-2">
              <div className="rounded-lg border bg-amber-50 border-amber-200 p-3 text-sm">
                <div className="flex justify-between py-0.5">
                  <span className="text-slate-600">Títulos que serão removidos:</span>
                  <strong className="text-amber-800" data-testid="revert-found">{revertDryRun.titles_found}</strong>
                </div>
                <div className="flex justify-between py-0.5">
                  <span className="text-slate-600">Títulos manuais preservados:</span>
                  <strong className="text-emerald-700" data-testid="revert-skipped">{revertDryRun.skipped_manual}</strong>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
                  Digite <strong>REVERTER</strong> para confirmar
                </label>
                <Input
                  value={revertConfirmText}
                  onChange={(e) => setRevertConfirmText(e.target.value)}
                  placeholder="REVERTER"
                  data-testid="revert-confirm-input"
                  className="mt-1 font-mono uppercase"
                  disabled={revertBusy}
                />
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRevertOpen(false)}
              disabled={revertBusy}
              data-testid="revert-cancel"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRevertApply}
              disabled={revertBusy || (revertConfirmText || '').trim().toUpperCase() !== 'REVERTER' || !revertDryRun}
              data-testid="revert-apply"
              className="bg-amber-600 hover:bg-amber-700"
            >
              {revertBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
              Confirmar reversão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
